import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as S from "./styles";
import { requestCallback } from "../../../lib/contactHelper";

const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "");
  
  // Limit to 10 digits for US phone numbers
  const limitedDigits = digitsOnly.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length === 0) return "";
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

export function PhoneInput({
  buttonText = "Request Callback",
  placeholder = "Enter phone number",
  onSubmit,
  variant = "primary",
}) {
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const validatePhoneField = (value) => {
    if (!value) {
      setPhoneError("");
      return false;
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = value.replace(/\D/g, "");
    
    // Just check if it has 10 digits (US format) - less strict validation
    if (digitsOnly.length === 10) {
      setPhoneError("");
      return true;
    }
    
    // Also allow 11 digits if it starts with 1
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      setPhoneError("");
      return true;
    }
    
    // Allow international format
    if (value.startsWith("+") && digitsOnly.length >= 10) {
      setPhoneError("");
      return true;
    }
    
    setPhoneError("Please enter a valid 10-digit phone number");
    return false;
  };

  const handleDefaultSubmit = (phoneNumber) => {
    const onSuccess = () => {
      navigate("/submission-thank-you?type=callback");
    };

    requestCallback({ phoneNumber }, onSuccess);
  };

  const normalizePhoneNumber = (value) => {
    if (!value) return "";
    
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    
    // If already in international format, return as-is
    if (value.startsWith("+")) {
      return value;
    }
    
    // If it's 10 digits (US format), add +1 prefix
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    
    // If it's 11 digits starting with 1, add + prefix
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      return `+${digitsOnly}`;
    }
    
    // Return original if we can't normalize
    return value;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      setPhoneError("Phone number is required");
      return;
    }
    if (!validatePhoneField(phoneNumber)) {
      return;
    }
    
    // Normalize phone number before submitting
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    (onSubmit || handleDefaultSubmit)(normalizedPhone);
    setPhoneNumber("");
    setPhoneError("");
  };

  return (
    <S.StyledForm onSubmit={handleSubmit}>
      <S.InputField
        id={`phone-input-${Math.random()}`}
        type="tel"
        value={phoneNumber}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          setPhoneNumber(formatted);
          setPhoneError("");
        }}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          setPhoneNumber(trimmed);
          validatePhoneField(trimmed);
        }}
        placeholder={placeholder || "(202) 972-5691"}
        required
        $hasError={!!phoneError}
      />
      <S.SubmitButton type="submit" $variant={variant} disabled={!!phoneError}>
        <S.ButtonText>{buttonText}</S.ButtonText>
        <S.ButtonIcon
          src="/images/icons/arrow-top-right-white.svg"
          alt="Submit icon"
        />
      </S.SubmitButton>
    </S.StyledForm>
  );
}
